/**
 * AuditModal — Search the server-side audit log.
 * Matches Castellan dark theme + history modal pattern:
 * - Filter controls at top (date range, action, call sign)
 * - Table only appears after hitting Search
 * - Columns: Timestamp | Action | Call Sign | Detail
 */
import { useState } from 'react'
import { workerGetV1 } from '../../../api/worker'
import { modalOverlay, modalBox, darkInput } from './modalShared'
import ModalHeader from './ModalHeader'

interface ServerLog {
  created_at: string
  action: string
  call_sign: string
  detail: string
}

const ACTION_OPTIONS = [
  'All Actions',
  'LOGIN', 'LOGOUT', 'REGISTER',
  'SYNC', 'STALE_PUSH_REJECTED',
  'MANUAL_CLEAR', 'ARCHIVE_SHIFT',
  'APPROVE_USER', 'REJECT_USER', 'DELETE_USER',
  'ROSTER_ADD', 'ROSTER_UPDATE', 'ROSTER_REMOVE',
  'PROFILE_UPDATE', 'PASSWORD_CHANGE', 'PASSWORD_RESET',
  'VIEW_AUDIT_LOG', 'PRINT_AUDIT_LOG',
  'CHANGE_INVITE_CODE', 'UPDATE_TENANT_CONFIG',
]

export default function AuditModal({ onClose }: { onClose: () => void }) {
  const [dateFrom,  setDateFrom]  = useState('')
  const [dateTo,    setDateTo]    = useState('')
  const [action,    setAction]    = useState('')
  const [csFilter,  setCsFilter]  = useState('')
  const [detailF,   setDetailF]   = useState('')
  const [results,   setResults]   = useState<ServerLog[]>([])
  const [loading,   setLoading]   = useState(false)
  const [searched,  setSearched]  = useState(false)

  async function doSearch() {
    setLoading(true)
    setSearched(true)
    try {
      const params: Record<string, string> = {}
      if (dateFrom) params.from = dateFrom
      if (dateTo)   params.to   = dateTo
      if (action && action !== 'All Actions') params.action = action
      if (csFilter) params.callSign = csFilter.toUpperCase()
      const res = await workerGetV1<{ success: boolean; logs?: ServerLog[] }>(
        '/api/v1/audit/log', params
      )
      setResults(res.success && res.logs ? res.logs : [])
    } catch {
      try { setResults(JSON.parse(localStorage.getItem('fom_audit_log') ?? '[]')) } catch { setResults([]) }
    }
    setLoading(false)
  }

  // Client-side detail filter (fast, no extra round-trip)
  const rows = detailF
    ? results.filter(r =>
        r.detail?.toLowerCase().includes(detailF.toLowerCase()) ||
        r.action?.toLowerCase().includes(detailF.toLowerCase())
      )
    : results

  const inp: React.CSSProperties = { ...darkInput, width: 'auto', flex: '0 0 auto' }

  const th: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textAlign: 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    background: '#0f1c2e',
  }
  const td: React.CSSProperties = {
    padding: '5px 10px',
    fontSize: 12,
    color: 'var(--text)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    whiteSpace: 'nowrap',
  }

  const actionColor: Record<string, string> = {
    LOGIN: '#22c55e', LOGOUT: '#94a3b8', REGISTER: '#3b82f6',
    SYNC: '#64748b', STALE_PUSH_REJECTED: '#f59e0b',
    MANUAL_CLEAR: '#ef4444', ARCHIVE_SHIFT: '#a78bfa',
    APPROVE_USER: '#22c55e', REJECT_USER: '#ef4444', DELETE_USER: '#ef4444',
    ROSTER_ADD: '#22c55e', ROSTER_UPDATE: '#3b82f6', ROSTER_REMOVE: '#f59e0b',
    PROFILE_UPDATE: '#94b8d8', PASSWORD_CHANGE: '#f59e0b', PASSWORD_RESET: '#f59e0b',
    VIEW_AUDIT_LOG: '#64748b', CHANGE_INVITE_CODE: '#a78bfa',
  }

  function fmtTs(ts: string) {
    try {
      return new Date(ts).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      })
    } catch { return ts }
  }

  return (
    <div style={modalOverlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...modalBox(920), display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        <ModalHeader title="📋 Audit Log" onClose={onClose} />

        {/* Filter row 1 */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>FROM:</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inp} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>TO:</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inp} />
          <select
            value={action}
            onChange={e => setAction(e.target.value)}
            style={{ ...inp, cursor: 'pointer', minWidth: 140 }}
          >
            {ACTION_OPTIONS.map(a => <option key={a}>{a}</option>)}
          </select>
          <input
            placeholder="Call Sign"
            value={csFilter}
            onChange={e => setCsFilter(e.target.value)}
            style={{ ...inp, width: 100 }}
          />
          <input
            placeholder="Search detail…"
            value={detailF}
            onChange={e => setDetailF(e.target.value)}
            style={{ ...inp, width: 160 }}
          />
        </div>

        {/* Row 2 — search button */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <button
            onClick={doSearch}
            style={{
              background: '#16a34a',
              border: 'none',
              borderRadius: 8,
              padding: '6px 20px',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 13,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '…' : 'Search'}
          </button>
          {searched && !loading && (
            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 700 }}>
              {rows.length} result{rows.length !== 1 ? 's' : ''} found
            </span>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {!searched ? null : loading ? (
            <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32, fontSize: 13 }}>
              Searching…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32, fontSize: 13 }}>
              No results found.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 650 }}>
                <thead>
                  <tr>
                    <th style={th}>Timestamp</th>
                    <th style={th}>Action</th>
                    <th style={th}>Call Sign</th>
                    <th style={{ ...th, width: '100%' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                    >
                      <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11 }}>
                        {fmtTs(r.created_at)}
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: actionColor[r.action] ?? '#94b8d8' }}>
                        {r.action}
                      </td>
                      <td style={{ ...td, color: 'var(--accent)', fontWeight: 600 }}>
                        {r.call_sign || '—'}
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'normal' }}>
                        {r.detail || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
